import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import API_URL from '../config';
import './CompanyPage.css';

// Parse **bold** markdown inline
function renderInline(text) {
  if (!text) return null;
  const parts = text.split(/\*\*(.+?)\*\*/gs);
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
  );
}

function isHtmlContent(str) {
  return str && /<[a-z][\s\S]*>/i.test(str);
}

// Parse comma/newline/semicolon separated items into array
function parseList(str) {
  if (!str) return [];
  return str.split(/[,\n;]+/).map(b => b.trim()).filter(Boolean);
}

const BENEFIT_LABELS = {
  medical: 'Medical Insurance',
  dental: 'Dental Insurance',
  retirement401k: '401K',
  retirement401kMatching: '401K Matching',
  performanceBonus: 'Performance Bonus',
  safetyBonus: 'Safety Bonus',
  attendanceBonus: 'Attendance Bonus',
};

// Extract **Benefits** and **Requirements** sections from description text.
// Returns { prose, benefits[], requirements[] }
// Prose has those sections removed so they don't double-render.
function parseDescriptionSections(text) {
  if (!text) return { prose: '', benefits: [], requirements: [] };

  const lines = text.split('\n');
  const benefits = [];
  const requirements = [];
  const proseLines = [];
  let mode = 'prose'; // 'prose' | 'benefits' | 'requirements'

  for (const line of lines) {
    const trimmed = line.trim();

    if (/^\*\*(benefits?|perks?)[^*]*\*\*\s*$/i.test(trimmed)) {
      mode = 'benefits';
      continue; // drop the header line — card provides its own
    }
    if (/^\*\*(requirements?|must\s*(pass|have)[^*]*|qualifications?)[^*]*\*\*\s*$/i.test(trimmed)) {
      mode = 'requirements';
      continue;
    }

    if (mode !== 'prose') {
      const bullet = trimmed.match(/^[-•*]\s+(.+)/);
      if (bullet) {
        if (mode === 'benefits') benefits.push(bullet[1].trim());
        else requirements.push(bullet[1].trim());
        continue;
      }
      if (trimmed === '') continue; // blank lines stay in section
      // Non-bullet non-empty line → end section, fall through to prose
      mode = 'prose';
    }
    proseLines.push(line);
  }

  return { prose: proseLines.join('\n').trim(), benefits, requirements };
}

function CompanyPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [modal, setModal] = useState(null); // 'apply' | 'refer' | null
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [copyDone, setCopyDone] = useState(false);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/company-pages/${slug}`);
        setPage(res.data.page);
        setOnboarding(res.data.onboarding);
      } catch (err) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [slug]);

  const handleFormChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleApply = async e => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/company-pages/${slug}/apply`, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        cdlType: form.cdlType,
        experience: form.experience,
        message: form.message
      });
      setSubmitted(true);
    } catch {
      alert('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefer = async e => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/company-pages/${slug}/refer`, {
        referrerName: form.referrerName,
        referrerPhone: form.referrerPhone,
        referrerEmail: form.referrerEmail,
        referredName: form.referredName,
        referredPhone: form.referredPhone,
        referredEmail: form.referredEmail,
        message: form.message
      });
      setSubmitted(true);
    } catch {
      alert('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const openModal = type => {
    setModal(type);
    setForm({});
    setSubmitted(false);
  };

  const closeModal = () => {
    setModal(null);
    setSubmitted(false);
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopyDone(true);
      setTimeout(() => setCopyDone(false), 2500);
    });
  };

  if (loading) {
    return (
      <div className="cp-loading">
        <div className="cp-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (notFound || !page) {
    return (
      <div className="cp-not-found">
        <div className="cp-not-found-inner">
          <h2>Page Not Found</h2>
          <p>This company page doesn't exist or hasn't been published yet.</p>
          <a href="https://mountainrecruiting.com" className="cp-btn-primary">Visit Mountain Recruiting</a>
        </div>
      </div>
    );
  }

  if (!page.is_published) {
    return (
      <div className="cp-not-found">
        <div className="cp-not-found-inner">
          <h2>Coming Soon</h2>
          <p>This page is not yet published.</p>
        </div>
      </div>
    );
  }

  const f = onboarding || {};
  const isLinehaul = f.serviceType === 'Linehaul';
  const isHiring = page.hiring_status === 'hiring';

  const paySummary = () => {
    if (isLinehaul) {
      const parts = [];
      if (f.lhPayPerMile && f.lhPayPerMileAmount) parts.push(`${f.lhPayPerMileAmount}/mile`);
      if (f.lhFlatDailyWage) parts.push('Flat daily wage');
      if (f.lhHourly) parts.push('Hourly');
      if (f.lhPayOther && f.lhPayOtherText) parts.push(f.lhPayOtherText);
      return parts.join(' · ') || null;
    } else {
      const parts = [];
      if (f.regularPay) parts.push(f.regularPay);
      if (f.payStructure) parts.push(f.payStructure);
      return parts.join(' · ') || null;
    }
  };

  const payFrequency = isLinehaul ? (f.lhPayPeriod || null) : (f.payPeriod || null);
  const vacationTime = isLinehaul ? f.lhVacationSickTime : f.vacationSickTime;

  // If description is HTML (from rich text editor), skip markdown parsing
  const descIsHtml = isHtmlContent(page.description);
  const { prose, benefits: descBenefits, requirements: descRequirements } = descIsHtml
    ? { prose: '', benefits: [], requirements: [] }
    : parseDescriptionSections(page.description);

  // Benefits: prefer description-parsed, fall back to onboarding fields
  const onboardingBenefits = isLinehaul
    ? parseList(f.lhBenefits)
    : [
        ...Object.entries(BENEFIT_LABELS).filter(([key]) => f[key]).map(([, label]) => label),
        ...parseList(f.otherBenefits),
        ...(vacationTime ? [`PTO / Sick Time: ${vacationTime}`] : []),
      ];
  const benefitsList = descBenefits.length > 0 ? descBenefits : onboardingBenefits;

  // Requirements: prefer description-parsed, fall back to prescreening
  const requirementsList = descRequirements.length > 0 ? descRequirements : parseList(f.prescreeningQuestions);

  const highlights = [
    f.technology && { icon: '🏢', label: 'Partner Brand', value: f.technology, color: 'green' },
    f.serviceType && { icon: '🚛', label: 'Service Type', value: f.serviceType, color: 'blue' },
    (isLinehaul ? f.lhWeeklySchedule : f.operatingDays) && { icon: '📅', label: 'Schedule', value: isLinehaul ? f.lhWeeklySchedule : f.operatingDays, color: 'blue' },
    payFrequency && { icon: '💵', label: 'Pay Frequency', value: payFrequency, color: 'gold' },
    paySummary() && { icon: '💰', label: 'Pay', value: paySummary(), color: 'gold' },
    (isLinehaul && f.lhIncentives) && { icon: '⭐', label: 'Incentives', value: f.lhIncentives, color: 'purple' },
    (f.billingCity || f.billingState) && { icon: '📍', label: 'Location', value: [f.billingCity, f.billingState].filter(Boolean).join(', '), color: 'orange' },
    f.driverTypes && { icon: '🪪', label: 'Driver Types', value: f.driverTypes, color: 'blue' },
    (isLinehaul ? f.lhDispatchTime : f.dispatchTime) && { icon: '⏰', label: 'Dispatch Time', value: isLinehaul ? f.lhDispatchTime : f.dispatchTime, color: 'teal' },
  ].filter(Boolean);

  return (
    <div className="cp-root">

      {/* ── Header ── */}
      <div className="cp-header">
        <div className="cp-header-inner">
          <div className="cp-header-top">
            <div>
              <div className="cp-company-name">{page.display_name || page.company_name}</div>
              {(page.location || f.terminalAddress) && (
                <div className="cp-location">📍 {page.location || f.terminalAddress}</div>
              )}
            </div>
            <button className="cp-share-btn" onClick={handleShare}>
              {copyDone ? '✓ Copied!' : '🔗 Share'}
            </button>
          </div>
          <div className="cp-badges">
            <span className={`cp-badge-status ${isHiring ? 'hiring' : 'full'}`}>
              {isHiring ? '🟢 Actively Hiring' : '🔴 Positions Full'}
            </span>
            {f.serviceType && <span className="cp-badge-type">{f.serviceType}</span>}
            {f.technology && <span className="cp-badge-brand">{f.technology}</span>}
          </div>

          {/* Inline pay teaser in header */}
          {paySummary() && (
            <div className="cp-header-pay">
              <span className="cp-header-pay-label">Compensation</span>
              <span className="cp-header-pay-value">{paySummary()}{payFrequency ? ` · ${payFrequency}` : ''}</span>
            </div>
          )}
        </div>
        <div className="cp-header-wave">
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="#f5f7f5" />
          </svg>
        </div>
      </div>

      <div className="cp-body">

        {/* ── CTA buttons ── */}
        {isHiring && (
          <div className="cp-cta-row">
            <button className="cp-btn-primary" onClick={() => openModal('apply')}>
              🚀 Apply Now
            </button>
            <button className="cp-btn-secondary" onClick={() => openModal('refer')}>
              👥 Refer a Driver
            </button>
          </div>
        )}

        {/* ── Referral bonus banner ── */}
        {page.referral_bonus && (
          <div className="cp-referral-banner">
            <span className="cp-referral-icon">🎁</span>
            <div>
              <div className="cp-referral-title">Referral Bonus Program</div>
              <div className="cp-referral-text">{page.referral_bonus}</div>
            </div>
          </div>
        )}

        {/* ── Company description ── */}
        {(descIsHtml ? page.description : prose) && (
          <div className="cp-section">
            <h2 className="cp-section-title">About the Role</h2>
            {descIsHtml
              ? <div className="cp-description cp-description--html" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(page.description) }} />
              : <div className="cp-description">{renderInline(prose)}</div>
            }
          </div>
        )}

        {/* ── Highlights grid ── */}
        {highlights.length > 0 && (
          <div className="cp-section">
            <h2 className="cp-section-title">At a Glance</h2>
            <div className="cp-highlights">
              {highlights.map((h, i) => (
                <div key={i} className={`cp-highlight-card cp-highlight-card--${h.color}`}>
                  <div className="cp-highlight-icon">{h.icon}</div>
                  <div className="cp-highlight-label">{h.label}</div>
                  <div className="cp-highlight-value">{h.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Benefits card ── */}
        {benefitsList.length > 0 && (
          <div className="cp-section">
            <div className="cp-card cp-card--green">
              <div className="cp-card-header">
                <span className="cp-card-icon">✅</span>
                <span className="cp-card-title">BENEFITS</span>
              </div>
              <div className="cp-card-grid">
                {benefitsList.map((b, i) => (
                  <div key={i} className="cp-card-item cp-card-item--green">{b}</div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Requirements card ── */}
        {requirementsList.length > 0 && (
          <div className="cp-section">
            <div className="cp-card cp-card--red">
              <div className="cp-card-header">
                <span className="cp-card-icon">🚫</span>
                <span className="cp-card-title">MUST PASS / HAVE</span>
              </div>
              <div className="cp-card-grid">
                {requirementsList.map((r, i) => (
                  <div key={i} className="cp-card-item cp-card-item--red">✓ {r}</div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Route / Schedule detail ── */}
        {!isLinehaul && (f.scheduleArea || f.typicalHours || f.typicalMiles) && (
          <div className="cp-section">
            <h2 className="cp-section-title">Route Details</h2>
            <div className="cp-detail-list">
              {f.scheduleArea && <div className="cp-detail-item"><span>Area</span><span>{f.scheduleArea}</span></div>}
              {f.typicalHours && <div className="cp-detail-item"><span>Typical Hours/Day</span><span>{f.typicalHours}</span></div>}
              {f.typicalMiles && <div className="cp-detail-item"><span>Typical Miles/Day</span><span>{f.typicalMiles}</span></div>}
              {f.reportTime && <div className="cp-detail-item"><span>Report Time</span><span>{f.reportTime}</span></div>}
              {f.newHireSchedule && <div className="cp-detail-item"><span>New Hire Schedule</span><span>{f.newHireSchedule}</span></div>}
            </div>
          </div>
        )}

        {isLinehaul && (f.lhAssignedLanes || f.lhTruckInfo) && (
          <div className="cp-section">
            <h2 className="cp-section-title">Linehaul Details</h2>
            <div className="cp-detail-list">
              {f.lhAssignedLanes && <div className="cp-detail-item"><span>Assigned Lanes</span><span>{f.lhAssignedLanes}</span></div>}
              {f.lhTruckInfo && <div className="cp-detail-item"><span>Trucks / Equipment</span><span>{f.lhTruckInfo}</span></div>}
            </div>
          </div>
        )}

        {/* ── Bottom CTA ── */}
        {isHiring && (
          <div className="cp-bottom-cta">
            <div className="cp-bottom-cta-text">
              <div className="cp-bottom-cta-icon">🚛</div>
              <h3>Ready to get behind the wheel?</h3>
              <p>Join the team at {page.display_name || page.company_name}. Apply in under 2 minutes.</p>
            </div>
            <div className="cp-cta-row">
              <button className="cp-btn-primary cp-btn-large" onClick={() => openModal('apply')}>🚀 Apply Now</button>
              <button className="cp-btn-secondary cp-btn-large" onClick={() => openModal('refer')}>👥 Refer a Driver</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Footer ── */}
      <footer className="cp-footer">
        <div className="cp-footer-inner">
          <span>Recruiting powered by</span>
          <a href="https://mountainrecruiting.com" target="_blank" rel="noopener noreferrer" className="cp-footer-brand">
            ⛰ Mountain Recruiting
          </a>
        </div>
      </footer>

      {/* ── Apply Modal ── */}
      {modal === 'apply' && (
        <div className="cp-modal-overlay" onClick={closeModal}>
          <div className="cp-modal" onClick={e => e.stopPropagation()}>
            <button className="cp-modal-close" onClick={closeModal}>✕</button>
            {submitted ? (
              <div className="cp-modal-success">
                <div className="cp-success-icon">✓</div>
                <h3>Application Submitted!</h3>
                <p>Our team will be in touch with you soon.</p>
                <button className="cp-btn-primary" onClick={closeModal}>Close</button>
              </div>
            ) : (
              <>
                <h2>Apply to {page.display_name || page.company_name}</h2>
                <form onSubmit={handleApply} className="cp-modal-form">
                  <div className="cp-field">
                    <label>Full Name *</label>
                    <input name="name" value={form.name || ''} onChange={handleFormChange} required placeholder="John Smith" />
                  </div>
                  <div className="cp-field">
                    <label>Phone Number *</label>
                    <input name="phone" value={form.phone || ''} onChange={handleFormChange} required placeholder="(555) 123-4567" type="tel" />
                  </div>
                  <div className="cp-field">
                    <label>Email</label>
                    <input name="email" value={form.email || ''} onChange={handleFormChange} placeholder="john@email.com" type="email" />
                  </div>
                  <div className="cp-field">
                    <label>CDL Type</label>
                    <select name="cdlType" value={form.cdlType || ''} onChange={handleFormChange}>
                      <option value="">Select CDL type</option>
                      <option>Class A CDL</option>
                      <option>Class B CDL</option>
                      <option>No CDL</option>
                    </select>
                  </div>
                  <div className="cp-field">
                    <label>Years of Driving Experience</label>
                    <select name="experience" value={form.experience || ''} onChange={handleFormChange}>
                      <option value="">Select experience</option>
                      <option>Less than 1 year</option>
                      <option>1–2 years</option>
                      <option>3–5 years</option>
                      <option>5+ years</option>
                    </select>
                  </div>
                  <div className="cp-field">
                    <label>Message (optional)</label>
                    <textarea name="message" value={form.message || ''} onChange={handleFormChange} rows={3} placeholder="Anything you'd like us to know..." />
                  </div>
                  <button type="submit" className="cp-btn-primary cp-btn-full" disabled={submitting}>
                    {submitting ? 'Submitting...' : '🚀 Submit Application'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Refer Modal ── */}
      {modal === 'refer' && (
        <div className="cp-modal-overlay" onClick={closeModal}>
          <div className="cp-modal" onClick={e => e.stopPropagation()}>
            <button className="cp-modal-close" onClick={closeModal}>✕</button>
            {submitted ? (
              <div className="cp-modal-success">
                <div className="cp-success-icon">✓</div>
                <h3>Referral Submitted!</h3>
                <p>Thanks for the referral — our team will reach out.</p>
                <button className="cp-btn-primary" onClick={closeModal}>Close</button>
              </div>
            ) : (
              <>
                <h2>Refer a Driver</h2>
                <p className="cp-modal-sub">Know someone who'd be a great fit? Send us their info.</p>
                <form onSubmit={handleRefer} className="cp-modal-form">
                  <p className="cp-modal-section-label">Your Info</p>
                  <div className="cp-field">
                    <label>Your Name *</label>
                    <input name="referrerName" value={form.referrerName || ''} onChange={handleFormChange} required placeholder="Your full name" />
                  </div>
                  <div className="cp-field-row">
                    <div className="cp-field">
                      <label>Your Phone</label>
                      <input name="referrerPhone" value={form.referrerPhone || ''} onChange={handleFormChange} type="tel" placeholder="(555) 123-4567" />
                    </div>
                    <div className="cp-field">
                      <label>Your Email</label>
                      <input name="referrerEmail" value={form.referrerEmail || ''} onChange={handleFormChange} type="email" placeholder="you@email.com" />
                    </div>
                  </div>
                  <p className="cp-modal-section-label" style={{ marginTop: 16 }}>Driver You're Referring</p>
                  <div className="cp-field">
                    <label>Driver's Name *</label>
                    <input name="referredName" value={form.referredName || ''} onChange={handleFormChange} required placeholder="Driver's full name" />
                  </div>
                  <div className="cp-field">
                    <label>Driver's Phone *</label>
                    <input name="referredPhone" value={form.referredPhone || ''} onChange={handleFormChange} required type="tel" placeholder="(555) 987-6543" />
                  </div>
                  <div className="cp-field">
                    <label>Driver's Email</label>
                    <input name="referredEmail" value={form.referredEmail || ''} onChange={handleFormChange} type="email" placeholder="driver@email.com" />
                  </div>
                  <div className="cp-field">
                    <label>Message (optional)</label>
                    <textarea name="message" value={form.message || ''} onChange={handleFormChange} rows={2} placeholder="Any context about this driver..." />
                  </div>
                  <button type="submit" className="cp-btn-primary cp-btn-full" disabled={submitting}>
                    {submitting ? 'Submitting...' : '👥 Submit Referral'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyPage;
